/**
 * Offline Status Indicator Component
 * Shows connection status and pending sync items
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Chip,
  Badge,
  IconButton,
  Tooltip,
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Alert
} from '@mui/material';
import {
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  Sync as SyncIcon,
  CloudUpload as CloudUploadIcon,
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { getSyncStats, initDB } from '../services/offlineStorage';
import { onSyncStatusChange, runFullSync, isOnline } from '../services/syncManager';
import { readSession } from '../services/submissionSession';

const OfflineStatusIndicator = ({ token, darkMode = false, pingMs = null, pingFailed = false }) => {
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [syncStats, setSyncStats] = useState(null);
  const [syncMessage, setSyncMessage] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // Load sync stats
  const loadSyncStats = useCallback(async () => {
    try {
      await initDB();
      const stats = await getSyncStats();
      setSyncStats(stats);
    } catch (error) {
      console.error('Failed to load sync stats:', error);
    }
  }, []);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      setSyncMessage('Connection restored');
      loadSyncStats();
    };

    const handleOffline = () => {
      setOnline(false);
      setSyncMessage('Working offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial load
    loadSyncStats();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [loadSyncStats]);

  // Subscribe to sync status changes
  useEffect(() => {
    const unsubscribe = onSyncStatusChange((status) => {
      if (status.ownerUserId && status.ownerUserId !== readSession()?.ownerUserId) return;
      setSyncMessage(status.message);
      
      if (status.type === 'syncing' || status.type === 'sync-start') {
        setSyncing(true);
      } else if (status.type === 'sync-complete' || status.type === 'sync-error') {
        setSyncing(false);
        setLastSyncTime(new Date());
        loadSyncStats();
      } else if (status.type === 'online') {
        setOnline(true);
      } else if (status.type === 'offline') {
        setOnline(false);
      }
    });

    return unsubscribe;
  }, [loadSyncStats]);

  // Periodic stats refresh
  useEffect(() => {
    const interval = setInterval(loadSyncStats, 30000);
    return () => clearInterval(interval);
  }, [loadSyncStats]);

  // Manual sync trigger
  const handleManualSync = async () => {
    if (!online || syncing) return;
    
    setSyncing(true);
    setSyncMessage('Syncing...');
    
    try {
      const result = await runFullSync(token);
      setSyncMessage(result?.stats && (result.stats.pendingIncidents || result.stats.errorIncidents || result.stats.queuedOperations)
        ? 'Saved work remains on this device. Review Pending calls.' : 'Saved calls have server confirmation.');
      setLastSyncTime(new Date());
    } catch (error) {
      setSyncMessage(`Sync failed: ${error.message}`);
    } finally {
      setSyncing(false);
      loadSyncStats();
    }
  };

  // Calculate pending count
  const pendingCount = syncStats 
    ? syncStats.pendingIncidents + syncStats.queuedOperations + syncStats.errorIncidents
    : 0;

  // Determine status icon and color
  const getStatusInfo = () => {
    // Build ping display string
    const pingDisplay = pingFailed ? 'NO LINK' : pingMs !== null ? `${pingMs}ms` : '';
    
    if (!online || pingFailed) {
      return {
        icon: <WifiOffIcon />,
        color: 'error',
        label: pingFailed ? 'NO LINK' : 'Offline'
      };
    }
    if (syncing) {
      return {
        icon: <SyncIcon sx={{ animation: 'spin 1s linear infinite' }} />,
        color: 'info',
        label: pingDisplay ? `Syncing · ${pingDisplay}` : 'Syncing'
      };
    }
    if (pendingCount > 0) {
      return {
        icon: <CloudUploadIcon />,
        color: 'warning',
        label: pingDisplay ? `${pendingCount} pending · ${pingDisplay}` : `${pendingCount} pending`
      };
    }
    
    // Determine color based on ping latency
    let color = 'success';
    if (pingMs !== null) {
      if (pingMs > 500) color = 'error';
      else if (pingMs > 200) color = 'warning';
    }
    
    return {
      icon: <WifiIcon />,
      color: color,
      label: pingDisplay ? `Online · ${pingDisplay}` : 'Online'
    };
  };

  const statusInfo = getStatusInfo();
  const open = Boolean(anchorEl);

  return (
    <>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      
      <Tooltip title={`${statusInfo.label}${pendingCount > 0 ? ` - ${pendingCount} items to sync` : ''}`}>
        <Badge 
          badgeContent={pendingCount > 0 ? pendingCount : null} 
          color="warning"
          max={99}
        >
          <Chip
            icon={statusInfo.icon}
            label={statusInfo.label}
            color={statusInfo.color}
            size="small"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              cursor: 'pointer',
              '& .MuiChip-icon': {
                color: 'inherit'
              }
            }}
          />
        </Badge>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 280, maxWidth: 350 }}>
          <Typography variant="h6" gutterBottom>
            Sync Status
          </Typography>

          {!online && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Saved calls retry when connected and signed in as their owner. Other saved work may need review.
            </Alert>
          )}

          {syncMessage && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {syncMessage}
            </Typography>
          )}

          <List dense>
            <ListItem>
              <ListItemIcon>
                {online ? <CheckCircleIcon color="success" /> : <ErrorIcon color="error" />}
              </ListItemIcon>
              <ListItemText 
                primary="Connection" 
                secondary={online ? 'Connected' : 'No internet'} 
              />
            </ListItem>

            {syncStats && (
              <>
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon color={syncStats.pendingIncidents > 0 ? 'warning' : 'disabled'} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Pending Incidents" 
                    secondary={`${syncStats.pendingIncidents} waiting to sync`}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <CloudUploadIcon color={syncStats.queuedOperations > 0 ? 'warning' : 'disabled'} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Queued Operations" 
                    secondary={`${syncStats.queuedOperations} operations`}
                  />
                </ListItem>

                {syncStats.errorIncidents > 0 && (
                  <ListItem>
                    <ListItemIcon>
                      <ErrorIcon color="error" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Failed Syncs" 
                      secondary={`${syncStats.errorIncidents} errors`}
                    />
                  </ListItem>
                )}

                <ListItem>
                  <ListItemIcon>
                    <CloudDoneIcon color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Synced Locally" 
                    secondary={`${syncStats.syncedIncidents} incidents`}
                  />
                </ListItem>
              </>
            )}
          </List>

          {lastSyncTime && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Last checked: {lastSyncTime.toLocaleTimeString()}
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={handleManualSync}
              disabled={!online || syncing}
              fullWidth
            >
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default OfflineStatusIndicator;
